import React, { useMemo, useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, Network } from "lucide-react";
import MenuRowButton from "../ui/MenuRowButton";
import Separator from "../ui/Separator";
import { JointIcon } from "../ui/Icons";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";
import {
  getSceneState,
  useActuators,
  useBodies,
  useJoints,
  useMaterials,
  useSceneActions,
  useSites,
  useTendon,
} from "../../stores/sceneStore";
import ActuatorAccordion from "./ActuatorAccordion";
import { updateTendonVisualization } from "../../runtime/tendonVisualizationBridge";
import { useSelectionStore } from "../../stores/selectionStore";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import TendonVisibilityToolbar from "../../selected-constraint-visibility/TendonVisibilityToolbar";
import { useInspectorMobileScenePeek } from "./inspectorMobileScenePeekContext";
import { canRemoveTendonMemberAt } from "../../utils/tendonMemberConstraints";
import { createDefaultStandardMaterial } from "../../utils/materialDefaults";
import { generateUuid } from "../../utils/id";
import { stripNamespacePrefix } from "../../utils/nameUtils";
import {
  collectSpatialTendonWrapGeoms,
  getInternalSpatialTendonWrapMaterialIds,
} from "../../utils/spatialTendonWrapGeoms";
import { INLINE_NAV_OVERFLOW_THRESHOLD } from "../../constants/ui";
import FixedTendonMembersSection from "./FixedTendonMembersSection";
import SpatialTendonRouteSection from "./SpatialTendonRouteSection";
import SpatialTendonWrapGeomsSection from "./SpatialTendonWrapGeomsSection";
import TendonTypeSection from "./TendonTypeSection";
import {
  DEFAULT_WRAP_COLOR,
  INLINE_SPATIAL_ROUTE_EDITOR_LIMIT,
  hexToColor,
  isScalarJoint,
  isSpatialTendon,
} from "./tendonPanelConstants";

const TendonPropertiesPanel = ({
  quickNavBackTarget = null,
  onQuickNavBack = null,
  quickNavForwardTarget = null,
  onQuickNavForward = null,
}) => {
  const selectedTendon = useSelectionStore((state) => state.selectedTendon);
  const selectJoint = useSelectionStore((state) => state.selectJoint);
  const selectGeom = useSelectionStore((state) => state.selectGeom);
  const setLastSelectionSource = useSelectionStore((state) => state.setLastSelectionSource);
  const tendon = useTendon(selectedTendon || "", undefined, Object.is);
  const joints = useJoints();
  const sites = useSites();
  const bodies = useBodies();
  const materials = useMaterials();
  const sceneActions = useSceneActions();
  const allActuators = useActuators();
  const inspectorMobileScenePeek = useInspectorMobileScenePeek();
  const authoringLocked = usePhysicsAuthoringLock();
  const tendonActuators = useMemo(
    () => Object.values(allActuators || {}).filter((a) => a?.tendonUuid === tendon?.uuid),
    [allActuators, tendon?.uuid]
  );
  const [, setActiveSubmenuKey] = useState(null);
  const [wrapSectionOpen, setWrapSectionOpen] = useState(false);
  const [fullRouteEditorOpen, setFullRouteEditorOpen] = useState(false);
  const tendonIsSpatial = isSpatialTendon(tendon);
  const spatialRoute = Array.isArray(tendon?.spatial) ? tendon.spatial : [];
  const showFullRouteEditor =
    !tendonIsSpatial ||
    spatialRoute.length <= INLINE_SPATIAL_ROUTE_EDITOR_LIMIT ||
    fullRouteEditorOpen;

  useEffect(() => {
    setFullRouteEditorOpen(false);
    setWrapSectionOpen(false);
  }, [selectedTendon]);

  const jointOptions = useMemo(
    () =>
      Object.values(joints || {})
        .filter(isScalarJoint)
        .map((j) => ({ value: j.uuid, label: stripNamespacePrefix(j.name) || j.uuid })),
    [joints]
  );
  const siteOptions = useMemo(
    () =>
      Object.values(sites || {})
        .filter(Boolean)
        .map((site) => ({ value: site.uuid, label: stripNamespacePrefix(site.name) || site.uuid })),
    [sites]
  );
  const geomOptions = useMemo(() => {
    if (!showFullRouteEditor) return [];
    const options = [];
    Object.values(bodies || {}).forEach((body) => {
      (body?.geoms || []).forEach((geom) => {
        const geomType = String(geom?.kind || geom?.type || "").toLowerCase();
        if (geomType !== "sphere" && geomType !== "cylinder") return;
        if (!geom?.name) return;
        const bodyLabel = stripNamespacePrefix(body?.name) || body?.uuid || "body";
        options.push({
          value: geom.name,
          label: `${stripNamespacePrefix(geom.name)} (${bodyLabel})`,
        });
      });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [bodies, showFullRouteEditor]);

  useEffect(() => {
    if (!tendon) {
      updateTendonVisualization([]);
      return;
    }
    updateTendonVisualization([tendon]);
    return () => updateTendonVisualization([]);
  }, [tendon]);

  const spatialWrapGeoms = useMemo(
    () => (wrapSectionOpen ? collectSpatialTendonWrapGeoms({ bodies, tendon, materials }) : []),
    [bodies, materials, tendon, wrapSectionOpen]
  );
  const tendonWrapMaterialIds = useMemo(() => {
    const ids = new Set();
    spatialWrapGeoms.forEach((entry) => {
      if (entry.materialUuid) ids.add(entry.materialUuid);
    });
    return [...ids];
  }, [spatialWrapGeoms]);
  const primaryWrapMaterial = tendonWrapMaterialIds[0]
    ? materials?.[tendonWrapMaterialIds[0]]
    : null;
  const wrapColor = primaryWrapMaterial?.color || DEFAULT_WRAP_COLOR;

  if (!tendon) return null;

  const canGoBack = !!quickNavBackTarget && typeof onQuickNavBack === "function";
  const canGoForward = !!quickNavForwardTarget && typeof onQuickNavForward === "function";
  const backLabel = canGoBack ? `Back (ALT + <-): ${quickNavBackTarget.label}` : "Back (ALT + <-)";
  const forwardLabel = canGoForward
    ? `Forward (ALT + ->): ${quickNavForwardTarget.label}`
    : "Forward (ALT + ->)";
  const tendonMembers = tendonIsSpatial
    ? []
    : (tendon.joints || [])
        .map((entry, index) => ({
          key: `${entry?.joint || "missing"}-${index}`,
          jointUuid: entry?.joint || "",
          joint: entry?.joint ? joints[entry.joint] : null,
          coef: entry?.coef,
        }))
        .filter((entry) => entry.jointUuid);
  const focusJoint = (jointUuid) => {
    if (!jointUuid) return;
    setLastSelectionSource("sceneTree");
    selectJoint(jointUuid);
    setActiveSubmenuKey(null);
    requestInspectorChromeFlash();
  };
  const focusGeom = (geomKey) => {
    if (!geomKey) return;
    setLastSelectionSource("sceneTree");
    selectGeom(geomKey);
    setActiveSubmenuKey(null);
    requestInspectorChromeFlash();
  };
  const jointMemberTreeIcon = (slot) => (
    <span className="relative inline-flex h-4 w-4 shrink-0" aria-hidden="true">
      <JointIcon className="h-4 w-4" />
      <span
        className="pointer-events-none absolute bottom-0 right-0 translate-x-px translate-y-px text-[8px] font-bold leading-none text-gray-100"
        style={{ textShadow: "0 0 2px rgb(0 0 0), 0 0 1px rgb(0 0 0)" }}
      >
        {slot}
      </span>
    </span>
  );
  const memberSubmenu = (
    <div className="w-56 max-w-[calc(100vw-3rem)] space-y-1">
      {tendonMembers.map((entry) => (
        <MenuRowButton key={entry.key} onClick={() => focusJoint(entry.jointUuid)}>
          {stripNamespacePrefix(entry.joint?.name) || entry.jointUuid}
        </MenuRowButton>
      ))}
    </div>
  );
  const memberQuickNavActions = (() => {
    if (tendonIsSpatial) {
      return [
        {
          key: "route",
          label: `Spatial Route (${spatialRoute.length})`,
          icon: <Network className="w-4 h-4" aria-hidden="true" />,
          disabled: true,
        },
      ];
    }
    const n = tendonMembers.length;
    if (n === 0) {
      return [
        {
          key: "members",
          label: "No joint members",
          icon: <JointIcon className="w-4 h-4" aria-hidden="true" />,
          disabled: true,
        },
      ];
    }
    if (n === 1) {
      return [
        {
          key: "members",
          label: `Inspect Joint: ${stripNamespacePrefix(tendonMembers[0].joint?.name) || tendonMembers[0].jointUuid}`,
          icon: <JointIcon className="w-4 h-4" aria-hidden="true" />,
          disabled: false,
          onDefaultClick: () => focusJoint(tendonMembers[0].jointUuid),
        },
      ];
    }
    if (n <= INLINE_NAV_OVERFLOW_THRESHOLD) {
      return tendonMembers.map((entry, index) => ({
        key: `member-${index}`,
        label: `Inspect Joint ${index + 1}: ${stripNamespacePrefix(entry.joint?.name) || entry.jointUuid}`,
        icon: jointMemberTreeIcon(index + 1),
        disabled: false,
        onDefaultClick: () => focusJoint(entry.jointUuid),
      }));
    }
    return [
      {
        key: "members",
        label: `Inspect Joint Members (${n})`,
        icon: <JointIcon className="w-4 h-4" aria-hidden="true" />,
        disabled: false,
        onDefaultClick: null,
        submenu: memberSubmenu,
      },
    ];
  })();
  const _quickNavActions = [
    {
      key: "back",
      label: backLabel,
      icon: <ArrowLeft className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoBack,
      onDefaultClick: () => {
        if (canGoBack) onQuickNavBack(quickNavBackTarget);
      },
    },
    {
      key: "forward",
      label: forwardLabel,
      icon: <ArrowRight className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoForward,
      onDefaultClick: () => {
        if (canGoForward) onQuickNavForward(quickNavForwardTarget);
      },
    },
    ...memberQuickNavActions,
  ];

  const updateTendon = (patch) => {
    if (!tendon.uuid) return;
    sceneActions.updateTendon(tendon.uuid, patch);
  };

  const makeFallbackSpatialRouteEntry = (kind = "site") => {
    if (kind === "geom") {
      return {
        kind: "geom",
        geom: geomOptions[0]?.value || "",
        ...(siteOptions[0] ? { sidesite: siteOptions[0].value } : {}),
      };
    }
    if (kind === "pulley") {
      return { kind: "pulley", divisor: 1 };
    }
    return { kind: "site", site: siteOptions[0]?.value || "" };
  };

  const makeFallbackSpatialRoute = () => {
    const firstSite = siteOptions[0]?.value || "";
    const secondSite = siteOptions.find((option) => option.value !== firstSite)?.value || "";
    return firstSite && secondSite
      ? [
          { kind: "site", site: firstSite },
          { kind: "site", site: secondSite },
        ]
      : firstSite
        ? [{ kind: "site", site: firstSite }]
        : [];
  };

  const handleChangeTendonType = (nextKind) => {
    if (nextKind === tendon.kind) return;
    if (nextKind === "spatial") {
      updateTendon({
        kind: "spatial",
        joints: [],
        spatial: makeFallbackSpatialRoute(),
      });
      return;
    }
    const fallbackJointA = jointOptions[0]?.value || "";
    const fallbackJointB =
      jointOptions.find((option) => option.value !== fallbackJointA)?.value || "";
    updateTendon({
      kind: "fixed",
      spatial: undefined,
      joints:
        fallbackJointA && fallbackJointB
          ? [
              { joint: fallbackJointA, coef: 1 },
              { joint: fallbackJointB, coef: 1 },
            ]
          : [],
    });
  };

  const handleAddMember = () => {
    const existing = new Set((tendon.joints || []).map((entry) => entry?.joint).filter(Boolean));
    const fallbackJoint = jointOptions.find((option) => !existing.has(option.value))?.value || "";
    if (!fallbackJoint) return;
    updateTendon({ joints: [...(tendon.joints || []), { joint: fallbackJoint, coef: 1 }] });
  };

  const handleUpdateMemberJoint = (index, nextJointUuid) => {
    const nextMembers = [...(tendon.joints || [])];
    if (!nextMembers[index]) return;
    const duplicate = nextMembers.some(
      (member, memberIndex) => memberIndex !== index && member?.joint === nextJointUuid
    );
    if (duplicate) return;
    nextMembers[index] = { ...nextMembers[index], joint: nextJointUuid };
    updateTendon({ joints: nextMembers });
  };

  const handleUpdateMemberCoef = (index, nextCoefValue) => {
    const parsed = Number(nextCoefValue);
    const nextMembers = [...(tendon.joints || [])];
    if (!nextMembers[index]) return;
    nextMembers[index] = { ...nextMembers[index], coef: Number.isFinite(parsed) ? parsed : 0 };
    updateTendon({ joints: nextMembers });
  };

  const handleRemoveMember = (index) => {
    if (!canRemoveTendonMemberAt(tendon.joints, index)) return;
    updateTendon({
      joints: (tendon.joints || []).filter((_, memberIndex) => memberIndex !== index),
    });
  };
  const getJointOptionsForMember = (index) => {
    const currentJoint = tendon.joints?.[index]?.joint || "";
    const usedByOtherRows = new Set(
      (tendon.joints || [])
        .map((entry, memberIndex) => (memberIndex === index ? null : entry?.joint))
        .filter(Boolean)
    );
    return jointOptions.filter(
      (option) => option.value === currentJoint || !usedByOtherRows.has(option.value)
    );
  };
  const hasUnusedJointOption = (tendon.joints || []).length < jointOptions.length;

  const updateSpatialRoute = (nextRoute) => updateTendon({ spatial: nextRoute });
  const handleAddRouteEntry = (kind) => {
    updateSpatialRoute([...spatialRoute, makeFallbackSpatialRouteEntry(kind)]);
  };
  const handleUpdateRouteEntry = (index, patch) => {
    const nextRoute = [...spatialRoute];
    if (!nextRoute[index]) return;
    nextRoute[index] = { ...nextRoute[index], ...patch };
    updateSpatialRoute(nextRoute);
  };
  const handleChangeRouteKind = (index, kind) => {
    const nextRoute = [...spatialRoute];
    if (!nextRoute[index]) return;
    nextRoute[index] = makeFallbackSpatialRouteEntry(kind);
    updateSpatialRoute(nextRoute);
  };
  const handleRemoveRouteEntry = (index) => {
    updateSpatialRoute(spatialRoute.filter((_, routeIndex) => routeIndex !== index));
  };
  const handleRouteDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = Number(active.data.current?.index);
    const newIndex = Number(over.data.current?.index);
    if (!Number.isInteger(oldIndex) || !Number.isInteger(newIndex)) return;
    if (
      oldIndex < 0 ||
      oldIndex >= spatialRoute.length ||
      newIndex < 0 ||
      newIndex >= spatialRoute.length
    )
      return;
    const nextRoute = [...spatialRoute];
    const [entry] = nextRoute.splice(oldIndex, 1);
    nextRoute.splice(newIndex, 0, entry);
    updateSpatialRoute(nextRoute);
  };

  const ensureTendonWrapMaterialIds = (color) => {
    const scene = getSceneState();
    const internalWrapMaterialIds = getInternalSpatialTendonWrapMaterialIds({
      bodies: scene.bodies,
      tendons: scene.tendons,
      materials: scene.materials,
    });
    const editableMaterialIds = tendonWrapMaterialIds.filter((materialId) =>
      internalWrapMaterialIds.has(materialId)
    );
    if (editableMaterialIds.length > 0) return editableMaterialIds;
    if (spatialWrapGeoms.length === 0) return [];
    const materialUuid = generateUuid();
    const material = {
      ...createDefaultStandardMaterial(`${stripNamespacePrefix(tendon.name) || "tendon"}_wrap`, {
        color,
        metalness: 0,
        roughness: 0.7,
      }),
      uuid: materialUuid,
      transparent: color[3] < 1,
      alphaMode: color[3] < 1 ? "blend" : "opaque",
      opacity: color[3],
    };
    sceneActions.addMaterial(material);
    spatialWrapGeoms.forEach((entry) => {
      if (entry.bodyUuid && entry.geomUuid) {
        sceneActions.setGeomMaterial(entry.bodyUuid, String(entry.geomUuid), materialUuid);
      }
    });
    return [materialUuid];
  };

  const handleWrapColorChange = (hex) => {
    const nextColor = hexToColor(
      hex,
      Array.isArray(wrapColor) ? (wrapColor[3] ?? DEFAULT_WRAP_COLOR[3]) : DEFAULT_WRAP_COLOR[3]
    );
    ensureTendonWrapMaterialIds(nextColor).forEach((materialId) => {
      sceneActions.updateMaterial(materialId, {
        color: nextColor,
        transparent: nextColor[3] < 1,
        alphaMode: nextColor[3] < 1 ? "blend" : "opaque",
        opacity: nextColor[3],
      });
    });
  };

  const handleWrapAlphaChange = (event) => {
    const alpha = Math.max(0, Math.min(1, Number(event.target.value) || 0));
    const base = Array.isArray(wrapColor) ? wrapColor : DEFAULT_WRAP_COLOR;
    const nextColor = [base[0], base[1], base[2], alpha];
    ensureTendonWrapMaterialIds(nextColor).forEach((materialId) => {
      sceneActions.updateMaterial(materialId, {
        color: nextColor,
        transparent: alpha < 1,
        alphaMode: alpha < 1 ? "blend" : "opaque",
        opacity: alpha,
      });
    });
  };

  if (inspectorMobileScenePeek?.compact) {
    return (
      <div className="p-3 space-y-4">
        <TendonVisibilityToolbar tendonUuid={tendon.uuid} />
      </div>
    );
  }

  return (
    <div className="p-3 space-y-4">
      <TendonVisibilityToolbar tendonUuid={tendon.uuid} />

      <Separator />

      <TendonTypeSection tendon={tendon} onChangeTendonType={handleChangeTendonType} />

      <Separator />

      <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
        {tendonIsSpatial ? (
          <div className="space-y-3">
            <SpatialTendonRouteSection
              tendon={tendon}
              spatialRoute={spatialRoute}
              showFullRouteEditor={showFullRouteEditor}
              siteOptions={siteOptions}
              geomOptions={geomOptions}
              onAddRouteEntry={handleAddRouteEntry}
              onUpdateRouteEntry={handleUpdateRouteEntry}
              onChangeRouteKind={handleChangeRouteKind}
              onRemoveRouteEntry={handleRemoveRouteEntry}
              onRouteDragEnd={handleRouteDragEnd}
              onOpenFullRouteEditor={() => setFullRouteEditorOpen(true)}
            />
            <SpatialTendonWrapGeomsSection
              open={wrapSectionOpen}
              onOpenChange={setWrapSectionOpen}
              spatialWrapGeoms={spatialWrapGeoms}
              primaryWrapMaterial={primaryWrapMaterial}
              wrapColor={wrapColor}
              sites={sites}
              onWrapColorChange={handleWrapColorChange}
              onWrapAlphaChange={handleWrapAlphaChange}
              onFocusGeom={focusGeom}
            />
          </div>
        ) : (
          <FixedTendonMembersSection
            tendon={tendon}
            hasUnusedJointOption={hasUnusedJointOption}
            getJointOptionsForMember={getJointOptionsForMember}
            onAddMember={handleAddMember}
            onUpdateMemberJoint={handleUpdateMemberJoint}
            onUpdateMemberCoef={handleUpdateMemberCoef}
            onRemoveMember={handleRemoveMember}
          />
        )}
      </InspectorPhysicsLockedCollapsibleWrap>

      <ActuatorAccordion
        actuators={tendonActuators}
        sceneActions={sceneActions}
        addActuatorTendon={{ uuid: tendon.uuid, name: tendon.name }}
      />
    </div>
  );
};

export default React.memo(TendonPropertiesPanel);
